import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import { put, get } from "../../../../utils/api";
import styles from "../../../../styles/gri/general.module.css";
import AddmultipleTables from "./AddmultipleTables";
import GRISidebar from "../../../../Layout/GRISidebar";

import ReviewBox from "../../../../Components/Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";
import { truncateNumber } from "../../../../utils/common";

const GRI301_2 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);

  const [errors, setErrors] = useState({});
  const [addTables, setaddTables] = useState([
    {
      productName: "",
      measurementUnit: {
        mass: { active: true, remark: "" },
        volume: { active: false, remark: "" },
      },
      renewable: {
        totalRecycled: "",
        totaInputMaterials: "",
        percentage: "",
      },
      nonrenewable: {
        totalRecycled: "",
        totaInputMaterials: "",
        percentage: "",
      },
    },
  ]);
  // Total state kept for API compatibility but not used in render (using calculatedTotals instead)
  // eslint-disable-next-line no-unused-vars
  const [total, setTotal] = useState({
    totalInputMaterial: "",
    totalInputRecycled: "",
    percentage: "",
    percentageOfRecyled: "",
  });
  const [griReview, setGriReview] = useState(() =>
    JSON.parse(localStorage.getItem("griReview"))
  );

  const inputData = [
    {
      defalut: "Material Used",
      colwidth: "22%",
      fields: [
        {
          title: "Total Input Materials Used",
          type: "number",
          fieldName: "totaInputMaterials",
        },
        {
          title: "Total Recycled Input Materials Used",
          type: "number",
          fieldName: "totalRecycled",
        },
        {
          title: "Percentage of Recycled Input Materials Used",
          type: "number",
          fieldName: "percentage",
        },
      ],
    },
  ];

  const defaultSegments = [
    {
      title: "Non-Renewable Materials Used",
      field: "nonrenewable",
      width: "9%",
    },
    {
      title: "Renewable Materials Used",
      field: "renewable",
      width: "9%",
    },
  ];

  const DropDownData = {
    heading: "Measurement Unit:",
    fieldName: "measurementUnit",
    fields: [
      {
        title: "Mass",
        fields: "mass",
        subfileds: "active",
        list: ["Kg", "Tons"],
        listFields: "remark",
      },
      {
        title: "Volume",
        fieldName: "measurementUnit",
        fields: "volume",
        subfileds: "active",
        listFields: "remark",
        list: ["Litre", "Kilo Litre", "M3"],
      },
    ],
  };

  // Calculate totals using useMemo instead of useEffect
  const calculatedTotals = useMemo(() => {
    let totalInputMaterial = 0;
    let totalInputRecycled = 0;

    // Loop through each table entry to sum the values
    addTables.forEach((table) => {
      if (table.renewable) {
        totalInputMaterial += Number(table.renewable.totaInputMaterials) || 0;
        totalInputRecycled += Number(table.renewable.totalRecycled) || 0;
      }
      if (table.nonrenewable) {
        totalInputMaterial +=
          Number(table.nonrenewable.totaInputMaterials) || 0;
        totalInputRecycled += Number(table.nonrenewable.totalRecycled) || 0;
      }
    });

    // Calculate average percentage recycled
    const averagePercentageRecycled =
      totalInputMaterial > 0
        ? (totalInputRecycled / totalInputMaterial) * 100
        : 0;

    return {
      totalInputMaterial,
      totalInputRecycled,
      percentage: 100,
      percentageOfRecyled: averagePercentageRecycled,
    };
  }, [addTables]);

  // Calculate units using useMemo
  const calculatedUnits = useMemo(() => {
    return (
      addTables[0]?.measurementUnit?.mass?.remark ||
      addTables[0]?.measurementUnit?.volume?.remark ||
      "Kg"
    );
  }, [addTables]);

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const fetchData = useCallback(async () => {
    await get(`/gri/get?type=GRI300&Id=${id}`)
      .then((res) => {
        if (
          res?.data?.data?.year
            ? res?.data?.data?.year < currentYear - 1
            : false
        )
          setDisabled(true);
        const response = res.data.data.GRI300.recycledInput;
        const defaultTable = {
          productName: "",
          measurementUnit: {
            mass: {
              active:
                res.data.data.GRI300.materialUsedWeightOrVolumn?.[0]
                  ?.measurementUnit?.mass?.active || false,
              remark:
                res.data.data.GRI300.materialUsedWeightOrVolumn?.[0]
                  ?.measurementUnit?.mass?.remark || "",
            },
            volume: {
              active:
                res.data.data.GRI300.materialUsedWeightOrVolumn?.[0]
                  ?.measurementUnit?.volume?.active || false,
              remark:
                res.data.data.GRI300.materialUsedWeightOrVolumn?.[0]
                  ?.measurementUnit?.volume?.remark || "",
            },
          },
          renewable: {
            totalRecycled: "",
            totaInputMaterials: "",
            percentage: "",
          },
          nonrenewable: {
            totalRecycled: "",
            totaInputMaterials: "",
            percentage: "",
          },
        };
        setaddTables(
          response.length > 0
            ? response.map((item) => ({
                productName: item?.productName,
                measurementUnit: {
                  mass: {
                    active: item?.measurementUnit?.mass?.active,
                    remark: item?.measurementUnit?.mass?.remark,
                  },
                  volume: {
                    active: item?.measurementUnit?.volume?.active,
                    remark: item?.measurementUnit?.volume?.remark,
                  },
                },
                renewable: {
                  totalRecycled: item?.renewable?.totalRecycled,
                  totaInputMaterials: item?.renewable?.totaInputMaterials,
                  percentage: item?.renewable?.percentage,
                },
                nonrenewable: {
                  totalRecycled: item?.nonrenewable?.totalRecycled,
                  totaInputMaterials: item?.nonrenewable?.totaInputMaterials,
                  percentage: item?.nonrenewable?.percentage,
                },
              }))
            : [defaultTable]
        );
        // Totals will be calculated automatically from addTables via useMemo
        // Keeping setTotal for API compatibility but totals are calculated from addTables
        setTotal({
          totalInputMaterial: res.data.data.GRI300?.totalInputMaterial || 0,
          totalInputRecycled:
            res.data.data.GRI300?.totalInputRecyled ||
            res.data.data.GRI300?.totalInputRecycled ||
            0,
          percentage: res.data.data.GRI300?.percentage || 100,
          percentageOfRecyled: res.data.data.GRI300?.percentageOfRecyled || 0,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setaddTables((prev) => [
      ...prev,
      {
        productName: "",
        measurementUnit: {
          mass: {
            active:
              prev?.[prev.length - 1]?.measurementUnit?.mass?.active || false,
            remark:
              prev?.[prev.length - 1]?.measurementUnit?.mass?.remark || "",
          },
          volume: {
            active:
              prev?.[prev.length - 1]?.measurementUnit?.volume?.active || false,
            remark:
              prev?.[prev.length - 1]?.measurementUnit?.volume?.remark || "",
          },
        },
        renewable: {
          totalRecycled: "",
          totaInputMaterials: "",
          percentage: "",
        },
        nonrenewable: {
          totalRecycled: "",
          totaInputMaterials: "",
          percentage: "",
        },
      },
    ]);
  };

  const handleSubmit = async () => {
    if (!disabled) {
      try {
        await put(`/gri/update?Id=${id}`, {
          type: "GRI300",
          submitted301_2: true,
          recycledInput: [...addTables],
          ...calculatedTotals,
        })
          .then((res) => navigate(`/gri/gri300/301-3/${id}`))
          .catch((err) => console.log(err));
      } catch (error) {
        console.log(error);
        // setsnackOpen({
        //   open: true,
        //   message: error.data.message,
        //   severity: "error",
        // });
      }
    } else {
      navigate(`/gri/gri300/301-3/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p
          className={styles.section_heading}
          style={{ textDecoration: "none" }}
        >
          301 :{" "}
          <span className={styles.main_heading_underline}>Material Used</span>
        </p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        301-2 :{" "}
        <span className={styles.main_heading_underline}>
          Recycled input materials used
        </span>
      </p>
      <AddmultipleTables
        title="a. Percentage of recycled input materials used to manufacture the organization's primary products and services."
        {...{
          addTables,
          setaddTables,
          errors,
          setErrors,
          inputData,
          defaultSegments,
          DropDownData,
          handleAdd,
          setTotal,
          disabled: disabled,
          hide: hide,
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 3,
          mt: 3,
          marginLeft: "1.2rem",
        }}
      >
        <Box>
          <p style={{ fontWeight: "bold" }}>
            Total Input Material Used in {calculatedUnits}
          </p>
          <p>{truncateNumber(calculatedTotals.totalInputMaterial)}</p>
          {/* <p>Percentage</p>
          <p>100%</p> */}
          <p style={{ fontWeight: "bold" }}>
            Percentage of recycled input materials used
          </p>
          <p> {truncateNumber(calculatedTotals.percentageOfRecyled)}</p>
        </Box>
        <Box sx={{ marginBottom: "9%" }}>
          <p style={{ fontWeight: "bold" }}>
            Total Input Recycled Material Used in {calculatedUnits}
          </p>
          <p>{truncateNumber(calculatedTotals.totalInputRecycled)}</p>
          {/* <p>Percentage of recycled input materials used</p>
          <p> {total.percentageOfRecyled}</p> */}
          <p></p>
          <p></p>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ width: "98.2%", ml: 2 }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};
export default GRI301_2;
